import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { X, Smartphone, MessageCircle, Mic, MicOff, Send, Ruler, RotateCcw, ChevronRight, Trash2 } from 'lucide-react';
import { sendChatMessage, speechToText } from '../../../services/aiService';
import MeasureTool from '../../../utils/MeasureTool';

// ── Geometry analysis ────────────────────────────────────────────────────────
const analyzeGeometry = (scene) => {
  const meshes = [];
  scene.traverse(obj => { if (obj.isMesh) meshes.push(obj); });
  if (meshes.length === 0) return null;

  // Overall bounding box
  const overallBox = new THREE.Box3();
  meshes.forEach(m => overallBox.expandByObject(m));
  const overallSize = overallBox.getSize(new THREE.Vector3());

  // Individual mesh boxes sorted by volume
  const meshData = meshes.map(m => {
    const box = new THREE.Box3().setFromObject(m);
    const size = box.getSize(new THREE.Vector3());
    const vol = size.x * size.y * size.z;
    return { name: m.name || 'unnamed', size, volume: vol };
  }).sort((a, b) => b.volume - a.volume).slice(0, 8);

  const toFt = v => (v * 3.28084).toFixed(1);

  return {
    overall: {
      width: toFt(overallSize.x), height: toFt(overallSize.y), depth: toFt(overallSize.z),
      widthM: overallSize.x.toFixed(2), heightM: overallSize.y.toFixed(2), depthM: overallSize.z.toFixed(2),
    },
    meshCount: meshes.length,
    spaces: meshData.map(m => ({
      name: m.name,
      width: toFt(m.size.x), height: toFt(m.size.y), depth: toFt(m.size.z),
    })),
  };
};

const formatGeometryForAI = (geo, property) => {
  if (!geo) return '';
  let s = `\n\n3D MODEL SPATIAL DATA (auto-analyzed from geometry):\n`;
  s += `Overall model dimensions: ${geo.overall.width}ft wide × ${geo.overall.depth}ft deep × ${geo.overall.height}ft tall\n`;
  s += `(${geo.overall.widthM}m × ${geo.overall.depthM}m × ${geo.overall.heightM}m)\n`;
  s += `Total mesh components: ${geo.meshCount}\n`;
  if (geo.spaces.length > 0) {
    s += `\nLargest spatial components:\n`;
    geo.spaces.forEach((sp, i) => {
      s += `  ${i+1}. ${sp.name}: ${sp.width}ft × ${sp.depth}ft × ${sp.height}ft\n`;
    });
  }
  s += `\nUse these dimensions to estimate room sizes, ceiling heights, and wall dimensions when answering questions. The overall height is likely the ceiling height.`;
  return s;
};

// ── Component ────────────────────────────────────────────────────────────────
const ARModelViewer = ({ isOpen, property, onClose }) => {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animFrameRef = useRef(null);
  const measureToolRef = useRef(null);
  const scaleFactorRef = useRef(1); // real-world scale: multiply scene units by this to get real meters

  const [phase, setPhase] = useState('idle');
  const [loadProgress, setLoadProgress] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [isMeasureMode, setIsMeasureMode] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [geometryData, setGeometryData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isAILoading, setIsAILoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setPhase('idle'); setMessages([]); setConversationHistory([]);
      setMeasurements([]); setGeometryData(null); setIsMeasureMode(false);
    }
  }, [isOpen, property?.id]);

  useEffect(() => {
    if (isOpen && phase === 'idle' && modelUrl) setTimeout(() => startViewer(), 200);
  }, [isOpen, phase]);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape' && isOpen) handleClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen]);

  // Toggle measure mode
  useEffect(() => {
    if (!measureToolRef.current) return;
    if (isMeasureMode) measureToolRef.current.activate();
    else measureToolRef.current.deactivate();
  }, [isMeasureMode]);

  if (!isOpen || !property) return null;

  const getModelUrl = () => {
    if (property.modelUrl?.startsWith('https://')) return property.modelUrl;
    if (property.modelUrl?.startsWith('http://')) {
      return property.modelUrl.includes('localhost')
        ? property.modelUrl.replace('localhost', window.location.hostname)
        : property.modelUrl;
    }
    if (property.modelPath) {
      const safe = property.modelPath.startsWith('/') ? property.modelPath : `/${property.modelPath}`;
      const h = window.location.hostname;
      const prod = h === 'realestate3d-demo.com' || h.includes('pages.dev') || h.includes('workers.dev');
      return prod ? `https://realestate3d-backend.onrender.com${safe}` : `http://${h}:5000${safe}`;
    }
    return null;
  };
  const modelUrl = getModelUrl();

  // IP-230: Auto-generate 3-5 sentence property description
  const generateAutoDescription = async (geo) => {
    setShowChat(true);
    setIsAILoading(true);
    const geoContext = geo ? formatGeometryForAI(geo) : '';
    const prompt = `Generate a concise 3-5 sentence property description for a potential home buyer. Be specific, professional, and highlight key selling points. Use the spatial data and property details provided.`;
    try {
      const result = await sendChatMessage({
        modelId: property.id,
        message: prompt,
        measurements: [],
        conversationHistory: geoContext ? [
          { role: 'user', content: `[SPATIAL CONTEXT]${geoContext}` },
          { role: 'assistant', content: 'I have the spatial data ready.' }
        ] : [],
      });
      const aiText = result.success
        ? (result.data?.message || result.data?.response || result.response || 'Unable to generate description.')
        : 'Unable to generate description.';
      setMessages([{ type: 'ai', text: aiText, isAutoDescription: true }]);
      if (result.success) {
        setConversationHistory([
          { role: 'user', content: prompt },
          { role: 'assistant', content: aiText }
        ]);
      }
    } catch {
      setMessages([{ type: 'ai', text: 'Welcome! Ask me anything about this property.' }]);
    } finally {
      setIsAILoading(false);
    }
  };

  const startViewer = async () => {
    if (!modelUrl || !mountRef.current) return;
    setPhase('loading');
    const W = mountRef.current.clientWidth || 800;
    const H = mountRef.current.clientHeight || 500;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W, H);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = 2; bgCanvas.height = 512;
    const bgCtx = bgCanvas.getContext('2d');
    const grad = bgCtx.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0, '#050d1a'); grad.addColorStop(0.5, '#0a1628'); grad.addColorStop(1, '#0f1f35');
    bgCtx.fillStyle = grad; bgCtx.fillRect(0, 0, 2, 512);
    scene.background = new THREE.CanvasTexture(bgCanvas);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, W / H, 0.01, 1000);
    camera.position.set(3, 2, 4);
    cameraRef.current = camera;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = 0.06;
    controls.minDistance = 0.5; controls.maxDistance = 50;
    controls.autoRotate = true; controls.autoRotateSpeed = 0.8;
    controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xfff4e0, 0.4));
    const key = new THREE.DirectionalLight(0xfff8f0, 2.5);
    key.position.set(8, 12, 8); key.castShadow = true;
    key.shadow.mapSize.setScalar(2048); key.shadow.bias = -0.001;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xc8d8ff, 0.8);
    fill.position.set(-6, 4, -4); scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffcc88, 0.6);
    rim.position.set(0, -2, -8); scene.add(rim);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(30, 30),
      new THREE.MeshStandardMaterial({ color: 0x0a1628, roughness: 0.8, transparent: true, opacity: 0.6 })
    );
    ground.rotation.x = -Math.PI / 2; ground.position.y = -0.01; ground.receiveShadow = true;
    scene.add(ground);

    // Init MeasureTool
    measureToolRef.current = new MeasureTool(scene, camera, renderer, (measurement) => {
      // MeasureTool already applies scaleMultiplier, so distance is real-world meters
      setMeasurements(prev => [...prev, { 
        ...measurement,
        label: `Measurement ${prev.length + 1}` 
      }]);
    });

    const loader = new GLTFLoader();
    loader.load(modelUrl, (gltf) => {
      const model = gltf.scene;
      model.traverse(obj => {
        if (obj.isMesh) { obj.castShadow = true; obj.receiveShadow = true; if (obj.material) obj.material.needsUpdate = true; }
      });
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 2.5 / maxDim;
      // Store inverse scale so measurements can be converted back to real-world units
      // We don't know real size, but if sqft is provided we can estimate
      scaleFactorRef.current = 1 / scale;
      model.scale.setScalar(scale);
      model.position.sub(center.multiplyScalar(scale));
      model.position.y += size.y * scale * 0.5;
      scene.add(model);

      // Auto-analyze geometry
      const geo = analyzeGeometry(scene);
      setGeometryData(geo);

      // Update MeasureTool with real-world scale factor
      if (measureToolRef.current) {
        measureToolRef.current.scaleMultiplier = scaleFactorRef.current;
      }

      const fov = camera.fov * Math.PI / 180;
      const dist = (2.5 / 2) / Math.tan(fov / 2) * 2.2;
      camera.position.set(dist * 0.8, dist * 0.5, dist);
      controls.target.set(0, size.y * scale * 0.5, 0);
      controls.update();
      setPhase('ready');

      // IP-230: Auto-generate property description AFTER model is fully ready
      setTimeout(() => generateAutoDescription(geo), 800);
    }, (p) => { if (p.total > 0) setLoadProgress(Math.round(p.loaded / p.total * 100)); },
    (err) => { console.error(err); setPhase('ready'); });

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth, h = mountRef.current.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);
    renderer._resizeHandler = onResize;
  };

  const handleClose = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    measureToolRef.current?.dispose();
    if (rendererRef.current) {
      if (rendererRef.current._resizeHandler) window.removeEventListener('resize', rendererRef.current._resizeHandler);
      if (mountRef.current?.contains(rendererRef.current.domElement)) mountRef.current.removeChild(rendererRef.current.domElement);
      rendererRef.current.dispose(); rendererRef.current = null;
    }
    setPhase('idle'); onClose();
  };

  const launchAR = () => {
    if (!modelUrl) return;
    const link = document.createElement('a');
    link.setAttribute('rel', 'ar');
    link.appendChild(document.createElement('img'));
    link.setAttribute('href', modelUrl);
    link.click();
  };

  const handleToggleMeasure = () => {
    setIsMeasureMode(p => !p);
    if (controlsRef.current) controlsRef.current.autoRotate = false;
  };

  const handleClearMeasurements = () => {
    measureToolRef.current?.clearAll();
    setMeasurements([]);
  };

  const handleSendMessage = async (text) => {
    const msg = text || inputText.trim();
    if (!msg) return;
    setMessages(prev => [...prev, { type: 'user', text: msg }]);
    setInputText('');
    setIsAILoading(true);

    // Build enriched conversation history with geometry context
    const geoContext = formatGeometryForAI(geometryData, property);
    const enrichedHistory = conversationHistory.length === 0 && geoContext
      ? [{ role: 'user', content: `[CONTEXT]${geoContext}` }, { role: 'assistant', content: 'I have analyzed the 3D model geometry and have spatial data available to answer your questions.' }]
      : conversationHistory;

    // Add measurements to message if any exist
    const measureContext = measurements.length > 0
      ? `\n\nUser measurements taken: ${measurements.map((m, i) => `${m.label}: ${m.distance.toFixed(2)}m (${(m.distance * 3.28084).toFixed(1)}ft)`).join(', ')}`
      : '';

    try {
      const result = await sendChatMessage({
        modelId: property.id,
        message: msg + measureContext,
        measurements,
        conversationHistory: enrichedHistory,
      });
      const aiText = result.success
        ? (result.data?.message || result.data?.response || result.response || 'No response.')
        : (result.error || 'Error.');
      setMessages(prev => [...prev, { type: result.success ? 'ai' : 'error', text: aiText }]);
      if (result.success) setConversationHistory(prev => [...prev, { role: 'user', content: msg }, { role: 'assistant', content: aiText }]);
    } catch {
      setMessages(prev => [...prev, { type: 'error', text: 'Something went wrong.' }]);
    } finally {
      setIsAILoading(false);
    }
  };

  const handleVoice = async () => {
    if (isListening) return;
    setIsListening(true);
    try {
      const t = await speechToText();
      setIsListening(false);
      if (t) handleSendMessage(t);
    } catch {
      setIsListening(false);
      setMessages(prev => [...prev, { type: 'error', text: 'Voice not supported or mic denied.' }]);
    }
  };

  const price = property.price > 0 ? `$${Number(property.price).toLocaleString()}` : null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=DM+Sans:wght@300;400;500&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:0.4;transform:scale(0.85)}50%{opacity:1;transform:scale(1)}}
        @keyframes bounce{0%,80%,100%{transform:scale(0.4);opacity:0.3}40%{transform:scale(1);opacity:1}}
        @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
        .ar-hbtn:hover{transform:translateY(-1px);box-shadow:0 8px 24px rgba(212,175,55,0.35)!important}
        .ar-cbtn:hover{background:rgba(59,130,246,0.25)!important}
        .ar-mbtn:hover{background:rgba(34,197,94,0.2)!important}
        .ar-ctrl:hover{background:rgba(255,255,255,0.1)!important}
        .ar-sugg:hover{background:rgba(212,175,55,0.1)!important;border-color:rgba(212,175,55,0.35)!important;color:#d4af37!important}
        .ar-send:hover:not(:disabled){background:#b8960f!important}
        .ar-input:focus{border-color:rgba(212,175,55,0.4)!important;outline:none}
        .ar-close:hover{background:rgba(239,68,68,0.25)!important}
        .ar-prog{background:linear-gradient(90deg,#d4af37,#f0d060,#d4af37);background-size:200% auto;animation:shimmer 1.5s linear infinite}
        .ar-measure:hover{background:rgba(34,197,94,0.2)!important}
        .ar-measure.active{background:rgba(34,197,94,0.25)!important;border-color:rgba(34,197,94,0.6)!important}
      `}</style>

      <div style={{ position:'fixed',inset:0,background:'rgba(2,6,15,0.92)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:'0.75rem',backdropFilter:'blur(4px)',animation:'fadeUp 0.25s ease' }}
        onClick={e => e.target===e.currentTarget && handleClose()}>
        <div style={{ background:'linear-gradient(145deg,#050d1a 0%,#0a1628 60%,#0d1e34 100%)',borderRadius:'1.25rem',width:'100%',maxWidth:'1140px',height:'90vh',display:'flex',flexDirection:'column',overflow:'hidden',border:'1px solid rgba(212,175,55,0.15)',boxShadow:'0 40px 80px -12px rgba(0,0,0,0.8),inset 0 1px 0 rgba(255,255,255,0.05)' }}>

          {/* Header */}
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'1rem 1.5rem',borderBottom:'1px solid rgba(212,175,55,0.1)',flexShrink:0,background:'rgba(5,13,26,0.6)',backdropFilter:'blur(20px)' }}>
            <div>
              <div style={{ fontFamily:'"Cormorant Garamond",serif',fontSize:'1.5rem',fontWeight:300,color:'#f5edd6',letterSpacing:'0.02em',lineHeight:1 }}>{property.title}</div>
              <div style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.75rem',color:'#6b7f96',letterSpacing:'0.08em',textTransform:'uppercase',marginTop:'2px' }}>
                {property.address || property.location || 'Location TBD'}
                {price && <span style={{ marginLeft:'1rem',color:'#d4af37',fontWeight:500 }}>{price}</span>}
              </div>
            </div>
            <div style={{ display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap' }}>
              {/* AR */}
              <button className="ar-hbtn" onClick={launchAR} style={{ display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.5rem 1rem',background:'linear-gradient(135deg,#d4af37,#b8960f)',color:'#050d1a',border:'none',borderRadius:'0.625rem',fontSize:'0.75rem',fontWeight:700,cursor:'pointer',fontFamily:'"DM Sans",sans-serif',letterSpacing:'0.06em',textTransform:'uppercase',transition:'all 0.2s' }}>
                <Smartphone size={14} /> View in AR
              </button>
              {/* Measure */}
              <button className={`ar-mbtn ${isMeasureMode ? 'active' : ''}`} onClick={handleToggleMeasure} style={{ display:'flex',alignItems:'center',gap:'0.375rem',padding:'0.5rem 0.875rem',background:isMeasureMode?'rgba(34,197,94,0.2)':'rgba(34,197,94,0.08)',border:`1px solid ${isMeasureMode?'rgba(34,197,94,0.6)':'rgba(34,197,94,0.2)'}`,borderRadius:'0.625rem',color:'#4ade80',cursor:'pointer',fontSize:'0.75rem',fontFamily:'"DM Sans",sans-serif',fontWeight:500,transition:'all 0.2s' }}>
                <Ruler size={14} /> {isMeasureMode ? 'Stop Measuring' : 'Measure'}
              </button>
              {/* AI */}
              <button className="ar-cbtn" onClick={() => setShowChat(p=>!p)} style={{ display:'flex',alignItems:'center',gap:'0.375rem',padding:'0.5rem 0.875rem',background:showChat?'rgba(59,130,246,0.2)':'rgba(59,130,246,0.08)',border:`1px solid ${showChat?'rgba(59,130,246,0.6)':'rgba(59,130,246,0.2)'}`,borderRadius:'0.625rem',color:'#60a5fa',cursor:'pointer',fontSize:'0.75rem',fontFamily:'"DM Sans",sans-serif',fontWeight:500,transition:'all 0.2s' }}>
                <MessageCircle size={14} /> AI Guide
              </button>
              <button className="ar-close" onClick={handleClose} style={{ background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:'0.625rem',padding:'0.5rem',color:'#f87171',cursor:'pointer',display:'flex',transition:'all 0.15s' }}>
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ flex:1,display:'flex',overflow:'hidden',minHeight:0 }}>

            {/* Viewport */}
            <div style={{ flex:1,position:'relative',overflow:'hidden',cursor:isMeasureMode?'crosshair':'default' }}>
              <div ref={mountRef} style={{ width:'100%',height:'100%' }} />

              {/* Loading */}
              {phase==='loading' && (
                <div style={{ position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(5,13,26,0.85)',backdropFilter:'blur(8px)',gap:'1.5rem' }}>
                  <div style={{ width:'56px',height:'56px',border:'2px solid rgba(212,175,55,0.15)',borderTopColor:'#d4af37',borderRadius:'50%',animation:'spin 0.9s linear infinite' }} />
                  <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:'0.625rem' }}>
                    <span style={{ fontFamily:'"Cormorant Garamond",serif',color:'#f5edd6',fontSize:'1rem',fontWeight:300,letterSpacing:'0.1em',textTransform:'uppercase' }}>Loading Model</span>
                    <div style={{ width:'200px',height:'2px',background:'rgba(212,175,55,0.15)',borderRadius:'1px',overflow:'hidden' }}>
                      <div className="ar-prog" style={{ height:'100%',width:`${loadProgress}%`,borderRadius:'1px',transition:'width 0.3s ease' }} />
                    </div>
                    <span style={{ fontFamily:'"DM Sans",sans-serif',color:'#4a5e72',fontSize:'0.7rem' }}>{loadProgress}%</span>
                  </div>
                </div>
              )}

              {/* AR tip */}
              {phase==='ready' && !isMeasureMode && (
                <div style={{ position:'absolute',top:'1rem',left:'50%',transform:'translateX(-50%)',display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.4rem 0.875rem',background:'rgba(212,175,55,0.08)',border:'1px solid rgba(212,175,55,0.2)',borderRadius:'2rem',backdropFilter:'blur(12px)',pointerEvents:'none' }}>
                  <Smartphone size={11} color="#d4af37" />
                  <span style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.65rem',color:'#a89040' }}>Tap "View in AR" to place in your space</span>
                </div>
              )}

              {/* Measure mode indicator */}
              {isMeasureMode && (
                <div style={{ position:'absolute',top:'1rem',left:'50%',transform:'translateX(-50%)',display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.5rem 1rem',background:'rgba(34,197,94,0.15)',border:'1px solid rgba(34,197,94,0.4)',borderRadius:'2rem',backdropFilter:'blur(12px)',pointerEvents:'none' }}>
                  <Ruler size={12} color="#4ade80" />
                  <span style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.7rem',color:'#4ade80',fontWeight:600 }}>Tap two points on the model to measure</span>
                </div>
              )}

              {/* Controls hint */}
              {phase==='ready' && (
                <div style={{ position:'absolute',top:'1rem',left:'1rem',padding:'0.4rem 0.625rem',background:'rgba(5,13,26,0.55)',border:'1px solid rgba(255,255,255,0.05)',borderRadius:'0.5rem',backdropFilter:'blur(12px)' }}>
                  <p style={{ margin:0,fontFamily:'"DM Sans",sans-serif',fontSize:'0.6rem',color:'#2a3e52',lineHeight:1.8 }}>
                    Drag to rotate · Scroll to zoom · Right-click to pan
                  </p>
                </div>
              )}

              {/* Bottom controls */}
              {phase==='ready' && (
                <div style={{ position:'absolute',bottom:'1.25rem',left:'1.25rem',display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap' }}>
                  <button className="ar-ctrl" onClick={() => { controlsRef.current?.reset(); }} style={{ display:'flex',alignItems:'center',gap:'0.375rem',padding:'0.4rem 0.625rem',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'0.5rem',color:'#6b7f96',cursor:'pointer',fontSize:'0.65rem',fontFamily:'"DM Sans",sans-serif',backdropFilter:'blur(12px)',transition:'background 0.15s' }}>
                    <RotateCcw size={12} /><span>Reset</span>
                  </button>
                  {measurements.length > 0 && (
                    <button className="ar-ctrl" onClick={handleClearMeasurements} style={{ display:'flex',alignItems:'center',gap:'0.375rem',padding:'0.4rem 0.625rem',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:'0.5rem',color:'#f87171',cursor:'pointer',fontSize:'0.65rem',fontFamily:'"DM Sans",sans-serif',backdropFilter:'blur(12px)',transition:'background 0.15s' }}>
                      <Trash2 size={12} /><span>Clear ({measurements.length})</span>
                    </button>
                  )}
                </div>
              )}

              {/* Property stat cards */}
              {phase==='ready' && (property.beds>0||property.baths>0||property.sqft>0) && (
                <div style={{ position:'absolute',bottom:'1.25rem',right:'1.25rem',display:'flex',gap:'0.5rem',flexWrap:'wrap',justifyContent:'flex-end' }}>
                  {[
                    property.beds>0&&{val:property.beds,label:'Beds'},
                    property.baths>0&&{val:property.baths,label:'Baths'},
                    property.sqft>0&&{val:Number(property.sqft).toLocaleString(),label:'Sqft'},
                  ].filter(Boolean).map(({val,label})=>(
                    <div key={label} style={{ display:'flex',flexDirection:'column',alignItems:'center',padding:'0.4rem 0.75rem',background:'rgba(5,13,26,0.75)',border:'1px solid rgba(212,175,55,0.15)',borderRadius:'0.625rem',backdropFilter:'blur(16px)' }}>
                      <span style={{ fontFamily:'"Cormorant Garamond",serif',fontSize:'1.125rem',fontWeight:600,color:'#d4af37',lineHeight:1 }}>{val}</span>
                      <span style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.6rem',color:'#4a5e72',textTransform:'uppercase',letterSpacing:'0.1em',marginTop:'2px' }}>{label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Geometry badge */}
              {geometryData && phase==='ready' && (
                <div style={{ position:'absolute',top:'3rem',right:'1rem',padding:'0.4rem 0.625rem',background:'rgba(5,13,26,0.7)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:'0.5rem',backdropFilter:'blur(12px)' }}>
                  <p style={{ margin:0,fontFamily:'"DM Sans",sans-serif',fontSize:'0.6rem',color:'#2a5a3a',lineHeight:1.7 }}>
                    📐 {geometryData.overall.width}ft × {geometryData.overall.depth}ft × {geometryData.overall.height}ft<br/>
                    <span style={{ color:'#1e3a2a' }}>AI has spatial data</span>
                  </p>
                </div>
              )}
            </div>

            {/* AI Chat Panel */}
            {showChat && (
              <div style={{ width:'290px',flexShrink:0,borderLeft:'1px solid rgba(212,175,55,0.1)',background:'linear-gradient(180deg,#050d1a 0%,#070f1e 100%)',display:'flex',flexDirection:'column',animation:'fadeUp 0.2s ease' }}>
                <div style={{ padding:'0.875rem 1.125rem',borderBottom:'1px solid rgba(212,175,55,0.08)',flexShrink:0 }}>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:'0.5rem' }}>
                      <div style={{ width:'6px',height:'6px',borderRadius:'50%',background:'#d4af37',animation:'pulse 2s infinite' }} />
                      <span style={{ fontFamily:'"Cormorant Garamond",serif',fontSize:'1rem',fontWeight:400,color:'#f5edd6' }}>AI Property Guide</span>
                    </div>
                    <button onClick={()=>setShowChat(false)} style={{ background:'none',border:'none',color:'#3a4e62',cursor:'pointer',padding:'2px',display:'flex' }}><X size={14} /></button>
                  </div>
                  <p style={{ margin:'0.25rem 0 0',fontFamily:'"DM Sans",sans-serif',fontSize:'0.68rem',color:'#3a4e62' }}>
                    {geometryData ? '✓ Spatial data loaded · Ask about dimensions' : 'Ask about this property'}
                  </p>
                </div>

                <div style={{ flex:1,overflowY:'auto',padding:'0.75rem',display:'flex',flexDirection:'column',gap:'0.5rem' }}>
                  {messages.length===0 && (
                    <div style={{ display:'flex',flexDirection:'column',gap:'0.4rem',paddingTop:'0.25rem' }}>
                      <p style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.68rem',color:'#2a3e52',textAlign:'center',margin:'0 0 0.375rem' }}>Suggested questions</p>
                      {[
                        'What are the room dimensions?',
                        'How tall are the ceilings?',
                        'How many bedrooms and bathrooms?',
                        'What is the total square footage?',
                        'Describe this property for a buyer',
                        'What is the price?',
                      ].map(s=>(
                        <button key={s} className="ar-sugg" onClick={()=>handleSendMessage(s)} style={{ background:'rgba(212,175,55,0.05)',border:'1px solid rgba(212,175,55,0.12)',borderRadius:'0.5rem',padding:'0.4rem 0.625rem',color:'#7a6030',fontSize:'0.7rem',cursor:'pointer',textAlign:'left',fontFamily:'"DM Sans",sans-serif',transition:'all 0.15s',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                          <span>{s}</span><ChevronRight size={11} style={{ opacity:0.35,flexShrink:0 }} />
                        </button>
                      ))}
                    </div>
                  )}

                  {messages.map((m,i)=>(
                    <div key={i} style={{ alignSelf:m.type==='user'?'flex-end':'flex-start',maxWidth:'92%',animation:'fadeUp 0.2s ease' }}>
                      {m.type!=='user'&&<div style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.58rem',color: m.isAutoDescription ? '#d4af37' : '#2a3e52',marginBottom:'3px',textTransform:'uppercase',letterSpacing:'0.08em' }}>{m.type==='error'?'Error': m.isAutoDescription ? '✦ Property Overview' : 'AI Guide'}</div>}
                      <div style={{ padding:'0.5rem 0.625rem',borderRadius:m.type==='user'?'0.625rem 0.625rem 0.125rem 0.625rem':'0.125rem 0.625rem 0.625rem 0.625rem',background:m.type==='user'?'linear-gradient(135deg,rgba(212,175,55,0.2),rgba(184,150,15,0.15))':m.type==='error'?'rgba(239,68,68,0.1)':'rgba(255,255,255,0.04)',border:`1px solid ${m.type==='user'?'rgba(212,175,55,0.25)':m.type==='error'?'rgba(239,68,68,0.2)':'rgba(255,255,255,0.06)'}` }}>
                        <p style={{ margin:0,fontSize:'0.73rem',color:m.type==='error'?'#fca5a5':m.type==='user'?'#f5edd6':'#94a3b8',lineHeight:1.55,fontFamily:'"DM Sans",sans-serif' }}>{m.text}</p>
                      </div>
                    </div>
                  ))}

                  {isAILoading&&(
                    <div style={{ alignSelf:'flex-start',padding:'0.625rem 0.75rem',borderRadius:'0.125rem 0.625rem 0.625rem 0.625rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ display:'flex',gap:'3px',alignItems:'center' }}>
                        {[0,1,2].map(i=><div key={i} style={{ width:'5px',height:'5px',borderRadius:'50%',background:'#d4af37',animation:`bounce 1.2s ${i*0.18}s infinite ease-in-out` }} />)}
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Measurements display */}
                {measurements.length>0&&(
                  <div style={{ padding:'0.5rem 0.75rem',borderTop:'1px solid rgba(34,197,94,0.1)',flexShrink:0,background:'rgba(34,197,94,0.03)' }}>
                    <p style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.6rem',color:'#2a5a3a',margin:'0 0 0.3rem',textTransform:'uppercase',letterSpacing:'0.06em' }}>📐 Your Measurements</p>
                    {measurements.slice(-3).map((m,i)=>(
                      <p key={i} style={{ margin:'0 0 2px',fontFamily:'"DM Sans",sans-serif',fontSize:'0.68rem',color:'#4ade80' }}>
                        {m.label}: {m.distance.toFixed(2)}m / {(m.distance*3.28084).toFixed(1)}ft
                      </p>
                    ))}
                  </div>
                )}

                <div style={{ padding:'0.625rem',borderTop:'1px solid rgba(212,175,55,0.08)',display:'flex',gap:'0.3rem',flexShrink:0 }}>
                  <input className="ar-input" value={inputText} onChange={e=>setInputText(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleSendMessage();}}} placeholder="Ask about this property..." style={{ flex:1,background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'0.5rem',padding:'0.45rem 0.625rem',color:'#d4c9b0',fontSize:'0.73rem',fontFamily:'"DM Sans",sans-serif',transition:'border-color 0.15s' }} disabled={isAILoading} />
                  <button onClick={handleVoice} disabled={isListening||isAILoading} style={{ borderRadius:'0.5rem',padding:'0.45rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',background:isListening?'rgba(239,68,68,0.2)':'rgba(255,255,255,0.05)',border:`1px solid ${isListening?'rgba(239,68,68,0.4)':'rgba(255,255,255,0.08)'}`,flexShrink:0,transition:'all 0.15s' }}>
                    {isListening?<MicOff size={13} color="#f87171"/>:<Mic size={13} color="#6b7f96"/>}
                  </button>
                  <button className="ar-send" onClick={()=>handleSendMessage()} disabled={!inputText.trim()||isAILoading} style={{ borderRadius:'0.5rem',padding:'0.45rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',background:inputText.trim()?'#d4af37':'rgba(212,175,55,0.1)',border:'1px solid rgba(212,175,55,0.2)',flexShrink:0,transition:'all 0.15s' }}>
                    <Send size={13} color={inputText.trim()?'#050d1a':'#4a5e72'} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.5rem 1.5rem',borderTop:'1px solid rgba(212,175,55,0.08)',flexShrink:0,background:'rgba(5,13,26,0.4)' }}>
            <div style={{ display:'flex',gap:'1.5rem' }}>
              {[property.beds>0&&`${property.beds} Beds`,property.baths>0&&`${property.baths} Baths`,property.sqft>0&&`${Number(property.sqft).toLocaleString()} Sqft`].filter(Boolean).map(t=>(
                <span key={t} style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.68rem',color:'#3a4e62',letterSpacing:'0.05em' }}>{t}</span>
              ))}
            </div>
            <div style={{ display:'flex',alignItems:'center',gap:'0.375rem' }}>
              <div style={{ width:'5px',height:'5px',borderRadius:'50%',background:phase==='ready'?'#22c55e':'#d4af37',animation:phase!=='ready'?'pulse 1.5s infinite':'none' }} />
              <span style={{ fontFamily:'"DM Sans",sans-serif',fontSize:'0.62rem',color:'#3a4e62',textTransform:'uppercase',letterSpacing:'0.08em' }}>
                {phase==='loading'?'Loading':phase==='ready'?'Live':'Initializing'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ARModelViewer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  property: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string, address: PropTypes.string, location: PropTypes.string,
    modelUrl: PropTypes.string, modelPath: PropTypes.string,
    beds: PropTypes.number, baths: PropTypes.number,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sqft: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onClose: PropTypes.func.isRequired,
};

export default ARModelViewer;
